import * as React from 'react'
import { useCallback } from 'react'
import type { JSX } from 'react'

import { useQuery } from '@apollo/client/react'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  Flex,
  Heading,
  Label,
  Skeleton,
  Textarea,
  Button,
  Separator,
  Text,
  toast,
  OrderedList,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import {
  ReferralProgramPage,
  ReferralProgramStatusQuery,
} from './ReferralProgramPage'
import type { ReferralProgramStatusResult } from './ReferralProgramPage'
import style from '../Settings.module.css'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'

import referAFriendStyle from './ReferAFriend.module.css'

export const GetOrganizationReferalCode = graphql(/* GraphQL */ `
  query organizationReferralCode($organizationId: String!) {
    organizationReferralCode(organizationId: $organizationId) {
      referralCode
    }
  }
`)

const howItWorksSteps = [
  'Find the unique referral link above for your organization.',
  'Share your link publicly or with someone who would enjoy using Buffer.',
  'Your friend signs up for their free Buffer account.',
  'You and your friend can grow on social and beyond.',
]

const ReferalCode = (): JSX.Element => {
  const { account } = useAccount()
  const { triggerAttributes, createNewPostInComposer } = usePostComposer()

  const organizationName = account?.currentOrganization?.name
  const organizationId = account?.currentOrganization?.id

  const { data, loading } = useQuery(GetOrganizationReferalCode, {
    variables: {
      organizationId: organizationId ?? '',
    },
    skip: !organizationId,
  })

  const referralCode = data?.organizationReferralCode?.referralCode
  const referralCodeLink = `https://buffer.com/join/${referralCode}`

  const openComposer = useCallback((): void => {
    void createNewPostInComposer({
      cta: 'publish-settings-referAFriend-shareNow-1',
      channels: 'all',
      prefillPostData: {
        text: `I’m using Buffer to grow an engaged audience on social and beyond. Give it a try and create your free (forever) account today: https://buffer.com/join/${referralCode}`,
      },
    })
  }, [createNewPostInComposer, referralCode])

  const handleCopyLink = ({ link }: { link: string }): void => {
    BufferTracker.referralLinkCopied({
      organizationId: organizationId || '',
    })

    navigator.clipboard
      .writeText(link)
      .then(() => {
        return toast.success('Referral link copied to clipboard')
      })
      .catch(() => {
        return toast.error('Failed to copy referral link')
      })
  }

  const handleShareNow = (): void => {
    BufferTracker.referralPostStarted({
      organizationId: organizationId || '',
    })

    openComposer()
  }

  return (
    <Flex gap="sm" direction="column" fullWidth>
      <Label htmlFor="organization-referral-link">
        {organizationName} referral link:
      </Label>
      <div className={referAFriendStyle.linkRow}>
        {loading || !referralCode ? (
          <Skeleton width="100%" height="20px" />
        ) : (
          <Textarea
            id="organization-referral-link"
            className={referAFriendStyle.referralLink}
            value={referralCodeLink}
            readOnly
            resize="auto"
            rows={2}
          />
        )}
        <div className={referAFriendStyle.linkActions}>
          <Button
            size="medium"
            variant="secondary"
            onClick={(): void => handleCopyLink({ link: referralCodeLink })}
            disabled={loading || !referralCode}
          >
            Copy Link
          </Button>
          <Button
            {...triggerAttributes}
            size="medium"
            variant="primary"
            onClick={handleShareNow}
            disabled={loading || !referralCode}
          >
            Share Now
          </Button>
        </div>
      </div>
    </Flex>
  )
}

const LegacyReferAFriend = (): JSX.Element => {
  return (
    <NewSettingsPageLayout title="Refer a Friend" gap="xl">
      <section className={style.fullWidth}>
        <Flex gap="sm" direction="column" fullWidth>
          <Text color="subtle">
            Enjoying Buffer? <br />
            Your friends will too! Share Buffer to help them grow an audience
            with a totally free account.
            <br />
            You&apos;ll also support Buffer&apos;s growth, allowing us to
            continue improving the service.
          </Text>
        </Flex>
      </section>

      <ReferalCode />
      <section className={style.fullWidth}>
        <Separator />

        <Flex gap="sm" direction="column" fullWidth>
          <Heading as="h2" size="small">
            How it works:
          </Heading>
          <Flex fullWidth gap="sm" direction="row" align="center">
            <OrderedList key=".0">
              {howItWorksSteps.map((step) => (
                <OrderedList.Item key={step}>{step}</OrderedList.Item>
              ))}
            </OrderedList>
          </Flex>
        </Flex>
      </section>
    </NewSettingsPageLayout>
  )
}

type PausedReferralView = 'loading' | 'error' | 'history' | 'legacy'

/**
 * Decides which page a paused organization sees, once its status read settles.
 *
 * Only an enrolled participant has V2 history to keep, so a settled non-member
 * status falls back to the page the organization saw before the program
 * existed. A read that fails or comes back without a status is not a non-member
 * answer: routing it to the legacy page would hand an enrolled participant an
 * alternate share link during an outage, so it asks for a retry instead.
 */
function pausedReferralView(args: {
  loading: boolean
  hasError: boolean
  statusTypename: ReferralProgramStatusResult['__typename'] | undefined
}): PausedReferralView {
  if (args.loading) {
    return 'loading'
  }

  if (args.hasError || args.statusTypename === undefined) {
    return 'error'
  }

  return args.statusTypename === 'ReferralProgramEnrolled'
    ? 'history'
    : 'legacy'
}

const PausedReferralLayout = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  return (
    <NewSettingsPageLayout title="Refer a Friend" gap="xl">
      <section className={style.fullWidth}>{children}</section>
    </NewSettingsPageLayout>
  )
}

const PausedReferralStatus = ({
  organizationId,
}: {
  organizationId: string
}): JSX.Element => {
  // A participant enrolled while the flag was on can hold a cached `available`
  // sharing status. Reading past the cache keeps a paused link from rendering
  // as shareable, and the page below reuses the entry this write leaves.
  const { data, loading, error, refetch } = useQuery(
    ReferralProgramStatusQuery,
    {
      variables: { input: { organizationId } },
      fetchPolicy: 'network-only',
    },
  )

  const handleRetry = (): void => {
    refetch().catch(() => {
      // useQuery already exposes a failed refetch through `error`.
    })
  }

  switch (
    pausedReferralView({
      loading,
      hasError: error !== undefined,
      statusTypename: data?.referralProgramStatus?.__typename,
    })
  ) {
    case 'loading':
      return (
        <PausedReferralLayout>
          <Skeleton width="100%" height="20px" />
        </PausedReferralLayout>
      )
    case 'error':
      return (
        <PausedReferralLayout>
          <Flex gap="sm" direction="column" fullWidth>
            <Text>We could not load your referral program status.</Text>
            <div>
              <Button size="medium" variant="secondary" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          </Flex>
        </PausedReferralLayout>
      )
    case 'history':
      return <ReferralProgramPage />
    case 'legacy':
      return <LegacyReferAFriend />
  }
}

/**
 * Acquisition is paused, not withdrawn: an enrolled organization keeps reading
 * its referral history and reward state while the flag is off.
 */
const PausedReferralProgram = (): JSX.Element => {
  const { account } = useAccount()
  const organizationId = account?.currentOrganization?.id

  // No organization means no status to read yet, not a non-member answer.
  if (organizationId === undefined) {
    return (
      <PausedReferralLayout>
        <Skeleton width="100%" height="20px" />
      </PausedReferralLayout>
    )
  }

  // Remounting on a switch keeps the organization the customer left from
  // deciding what the organization they switched to sees: the new read starts
  // from `loading`, with no result of its own and none carried over.
  return (
    <PausedReferralStatus
      key={organizationId}
      organizationId={organizationId}
    />
  )
}

const ReferAFriend = (): JSX.Element | null => {
  const { isEnabled, isReady } = useSplitEnabled('geid-referral-program')

  if (!isReady) {
    return null
  }

  return isEnabled ? (
    <ReferralProgramPage fetchPolicy="network-only" />
  ) : (
    <PausedReferralProgram />
  )
}

export default ReferAFriend
